import { useGameData } from '../hooks/useGameData';
import {
  Grid,
  Box,
  Heading,
  Text,
  Stat,
  StatHelpText,
  StatArrow,
  Card,
  CardBody,
} from '@chakra-ui/react';

const GameCard = ({ gamePk, selected, onClick }) => {
  const cardClassName = selected ? 'selected' : 'elevated';
  const borderColor = "card.border";
  const textColor = "hero.text";
  const mutedTextColor = "hero.subtext";
  
  const { data } = useGameData(gamePk);
  
  const gameData = data?.data?.gameData;
  if (!gameData) return null;

  const game_detail = (gameData.status?.detailedState || "").toLowerCase();
  const game_state = gameData.status?.abstractGameState || "";
  
  // Safari-safe date parsing
  const rawDateTime = gameData.datetime?.dateTime;
  const start_time = rawDateTime 
    ? new Date(rawDateTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) 
    : 'TBD';

  const home_team = gameData.teams?.home?.abbreviation || '---';
  const away_team = gameData.teams?.away?.abbreviation || '---';

  const lineScore = data?.data?.liveData?.linescore || {};
  const inning = lineScore.currentInning;
  const inning_half = lineScore.inningHalf;
  const home_score = lineScore.teams?.home?.runs;
  const away_score = lineScore.teams?.away?.runs;
  const outs = lineScore.outs;

  const trackInning = () => {
    let inningElement;

    switch (inning_half) {
      case 'Top':
        inningElement = (
          <Stat>
            <StatHelpText color={textColor} mb={0} fontSize="10px">
              <StatArrow type="increase" color="brand.red" />
              {inning}
            </StatHelpText>
          </Stat>
        );
        break;
      case 'Middle':
      case 'Bottom':
        inningElement = (
          <Stat>
            <StatHelpText color={textColor} mb={0} fontSize="10px">
              <StatArrow type="decrease" color="brand.red" />
              {inning}
            </StatHelpText>
          </Stat>
        );
        break;
      default:
        inningElement = <Text fontSize="10px" fontWeight="900" color={textColor} whiteSpace="nowrap">{game_state}</Text>;
    }
    return inningElement;
  };

  let statusElement;

  switch (true) {
    case game_detail === 'scheduled':
    case game_detail === 'pre-game':
    case game_detail === 'warmup':
      statusElement = <Text fontSize="10px" fontWeight="900" color={mutedTextColor} whiteSpace="nowrap">{start_time}</Text>;
      break;
    case game_detail.includes('delayed'):
      statusElement = (
        <Text fontSize="10px" fontWeight="900" color="orange.400">
          DELAY
        </Text>
      );
      break;
    case game_detail.includes('postponed'):
      statusElement = (
        <Text fontSize="10px" fontWeight="900" color="brand.red">
          PPD
        </Text>
      );
      break;
    case game_detail === 'in progress':
    case game_detail.includes('replay'):
    case game_detail.includes('challenge'):
    case game_detail.includes('review'):
      statusElement = trackInning();
      break;
    case game_detail === 'game over':
    case game_detail === 'final':
      statusElement = (
        <Text fontSize="10px" fontWeight="900" color={textColor}>
          FINAL
        </Text>
      );
      break;
    default:
      statusElement = (
        <Text fontSize="10px" fontWeight="900" color={textColor} whiteSpace="nowrap">
          {game_state.toUpperCase()}
        </Text>
      );
  }

  const showScores =
    game_detail === 'in progress' ||
    game_detail === 'final' ||
    game_detail.startsWith('completed early') ||
    game_detail === 'game over';

  let outElement;

  switch (outs) {
    case 1:
      outElement = (
        <Text fontSize="10px" color="brand.red">●○○</Text>
      );
      break;
    case 2:
      outElement = (
        <Text fontSize="10px" color="brand.red">●●○</Text>
      );
      break;
    case 3:
      outElement = (
        <Text fontSize="10px" color="brand.red">●●●</Text>
      );
      break;
    default:
      outElement = (
        <Text fontSize="10px" color={mutedTextColor}>○○○</Text>
      );
  }

  const showOuts = game_detail === 'in progress';

  return (
    <Card
      size="sm"
      w="full"
      minWidth={0}
      minHeight="58px"
      variant={cardClassName}
      onClick={() => onClick(gamePk)}
      borderRadius="none"
      border="1px solid"
      borderColor={borderColor}
      _hover={{ transform: "scale(1.02)", transition: "0.2s" }}
    >
      <CardBody p={2}>
        <Heading size="xs" fontWeight="extrabold" textTransform="uppercase" color={textColor}>
          <Grid templateColumns="1.1fr 0.5fr 1.2fr" gap={0} alignItems="center">
            <Box marginBottom="5px" whiteSpace="nowrap">{away_team}</Box>
            <Box textAlign="center" marginBottom="5px">{showScores ? away_score : null}</Box>
            <Box textAlign="end" marginBottom="5px">{statusElement}</Box>
            <Box whiteSpace="nowrap">{home_team}</Box>
            <Box textAlign="center">{showScores ? home_score : null}</Box>
            <Box textAlign="end">{showOuts ? outElement : null}</Box>
          </Grid>
        </Heading>
      </CardBody>
    </Card>
  );
};
export default GameCard;
